import { useMutation } from '@tanstack/react-query'
import { GlossariesApi } from '@ulysses-inc/harami_api_client'
import { useCallback } from 'react'
import { useSnackbar } from 'src/components/snackbar/useSnackbar'
import { baseClient } from 'src/state/middleware/saga/baseClient'
import { handleHTTPError } from 'src/state/middleware/saga/handleHttpError'

const downloadBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob)
  try {
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  } finally {
    URL.revokeObjectURL(url)
  }
}

type DownloadParams = {
  id: number
  fileName: string
}

const downloadGlossaryJobCsv = async ({
  id,
  fileName,
}: DownloadParams): Promise<void> => {
  const response = await baseClient
    .getApi(GlossariesApi)
    .getGlossaryJobCsvRaw({ id })
    .catch(handleHTTPError)
  const blob = await response.raw.blob()
  downloadBlob(blob, fileName)
}

export const useDownloadGlossaryCsv = () => {
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbar()

  const downloadMutation = useMutation({
    mutationFn: (params: DownloadParams) => downloadGlossaryJobCsv(params),
    onError: () => {
      showErrorSnackbar('登録済み用語データのダウンロードに失敗しました')
    },
    onSuccess: () => {
      showSuccessSnackbar('CSVファイルをダウンロードしました')
    },
  })

  const downloadGlossaryCsv = useCallback(
    (id: number, fileName: string) => {
      downloadMutation.mutate({ id, fileName })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- downloadMutation is unstable reference
    [downloadMutation.mutate],
  )

  return {
    downloadGlossaryCsv,
    isDownloading: downloadMutation.isPending,
  }
}
